/*
  assets/animations.js
  LOST SIGNAL — Motion Engine
  Extends Shopify Dawn (OS 2.0). No core files modified.

  Load once, from sections/header.liquid:
    {{ 'animations.css' | asset_url | stylesheet_tag }}
    <script src="{{ 'animations.js' | asset_url }}" defer="defer"></script>

  Drives the four things CSS cannot do alone:
    1. Scroll reveals   — adds .is-in when an element enters the viewport
    2. Parallax         — writes a transform on [data-parallax] layers
    3. Ripple           — spawns a wave at the exact click point on .ls-ripple
    4. Magnetic         — pulls [data-magnetic] elements toward the pointer

  Everything else in animations.css is pure CSS.

  ---------------------------------------------------------------------------
  PERFORMANCE

  - ONE rAF loop for parallax and magnets. Not one per element.
  - Rects are cached, never measured inside the loop. getBoundingClientRect
    forces layout; calling it every frame is what makes motion scripts jank.
  - Only transform and opacity are written. The browser recomposites; it
    never reflows.
  - The loop stops itself when nothing is moving.
  - IntersectionObserver handles reveals — no scroll listener for them at all.
  - Reduced motion: reveals are applied instantly, everything else is skipped.
  ---------------------------------------------------------------------------
*/

(function () {
  'use strict';

  if (window.LSMotion) return;

  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var FINE = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  var WIDE = window.matchMedia('(min-width: 990px)');

  /* ═══════════════════════════════════════════════════════════
     1. SCROLL REVEALS
     ═══════════════════════════════════════════════════════════ */

  var REVEAL_SELECTOR =
    '.ls-reveal, .ls-reveal-fade, .ls-reveal-left, .ls-reveal-right, ' +
    '.ls-reveal-scale, .ls-mask, .ls-mask-left, .ls-stagger, .ls-stagger-slow, ' +
    '[data-reveal]';

  var observer = null;

  function initReveals(scope) {
    var nodes = (scope || document).querySelectorAll(REVEAL_SELECTOR);

    /* Reduced motion, or no IO support: show everything immediately. */
    if (REDUCED || !('IntersectionObserver' in window)) {
      nodes.forEach(function (el) {
        el.classList.add('is-in');
      });
      return;
    }

    if (!observer) {
      observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('is-in');
            /* Reveal once. Re-animating on every scroll pass is nauseating. */
            observer.unobserve(entry.target);
          });
        },
        {
          threshold: 0.12,
          /* Fire slightly before the element reaches the fold, so the motion
             has already begun by the time it is properly in view. */
          rootMargin: '0px 0px -8% 0px'
        }
      );
    }

    nodes.forEach(function (el) {
      if (el.dataset.lsObserved === 'true') return;
      el.dataset.lsObserved = 'true';

      /* Anything already on screen at load reveals at once — no element
         should sit invisible above the fold waiting for a scroll. */
      var rect = el.getBoundingClientRect();
      if (rect.top < window.innerHeight * 0.9 && rect.bottom > 0) {
        window.requestAnimationFrame(function () {
          el.classList.add('is-in');
        });
        return;
      }

      observer.observe(el);
    });
  }

  /* ═══════════════════════════════════════════════════════════
     2. PARALLAX + 3. MAGNETIC — one shared loop
     ═══════════════════════════════════════════════════════════ */

  var parallaxItems = [];
  var magnetItems = [];

  var pointer = { x: 0, y: 0 };
  var running = false;
  var frame = null;
  var measureTimer = null;

  var MAGNET_RADIUS = 90;
  var MAGNET_STRENGTH = 0.3;
  var MAGNET_EASE = 0.18;

  function measure() {
    parallaxItems = [];
    magnetItems = [];

    document.querySelectorAll('[data-parallax], .ls-parallax').forEach(function (el) {
      if (el.offsetParent === null) return;

      var frameEl = el.closest('.ls-parallax-frame') || el.parentElement;

      parallaxItems.push({
        el: el,
        frame: frameEl,
        speed: parseFloat(el.dataset.parallaxSpeed) || 0.12,
        y: 0
      });
    });

    if (FINE && !REDUCED) {
      document.querySelectorAll('[data-magnetic]').forEach(function (el) {
        if (el.offsetParent === null) return;

        var rect = el.getBoundingClientRect();

        magnetItems.push({
          el: el,
          label: el.querySelector('span'),
          strength: parseFloat(el.dataset.magneticStrength) || MAGNET_STRENGTH,
          left: rect.left + window.scrollX,
          top: rect.top + window.scrollY,
          width: rect.width,
          height: rect.height,
          cx: rect.left + window.scrollX + rect.width / 2,
          cy: rect.top + window.scrollY + rect.height / 2,
          x: 0,
          y: 0
        });
      });
    }
  }

  function scheduleMeasure() {
    window.clearTimeout(measureTimer);
    measureTimer = window.setTimeout(measure, 150);
  }

  function tick() {
    var moving = false;
    var vh = window.innerHeight;

    /* ---- Parallax ---- */
    if (WIDE.matches) {
      for (var i = 0; i < parallaxItems.length; i++) {
        var p = parallaxItems[i];
        var rect = p.frame.getBoundingClientRect();

        /* Off screen: skip. No point transforming what nobody can see. */
        if (rect.bottom < -200 || rect.top > vh + 200) continue;

        var progress = (rect.top + rect.height / 2 - vh / 2) / vh;
        progress = Math.max(-1, Math.min(1, progress));

        var next = progress * -100 * p.speed;

        if (Math.abs(next - p.y) > 0.05) {
          p.y = next;
          p.el.style.transform = 'translate3d(0,' + p.y.toFixed(2) + 'px,0)';
          moving = true;
        }
      }
    }

    /* ---- Magnets ---- */
    var pageX = pointer.x + window.scrollX;
    var pageY = pointer.y + window.scrollY;

    for (var j = 0; j < magnetItems.length; j++) {
      var m = magnetItems[j];

      /* Distance to the element's BOX, not its centre. Centre-based distance
         behaves badly on wide buttons — the pull kicks in unevenly. */
      var dx = Math.max(m.left - pageX, 0, pageX - (m.left + m.width));
      var dy = Math.max(m.top - pageY, 0, pageY - (m.top + m.height));
      var dist = Math.sqrt(dx * dx + dy * dy);

      var tx = 0;
      var ty = 0;

      if (dist < MAGNET_RADIUS) {
        var falloff = 1 - dist / MAGNET_RADIUS;
        tx = (pageX - m.cx) * m.strength * falloff;
        ty = (pageY - m.cy) * m.strength * falloff;
      }

      m.x += (tx - m.x) * MAGNET_EASE;
      m.y += (ty - m.y) * MAGNET_EASE;

      if (Math.abs(m.x) < 0.25 && Math.abs(m.y) < 0.25) {
        if (m.el.style.transform) {
          m.el.style.transform = '';
          if (m.label) m.label.style.transform = '';
        }
      } else {
        moving = true;
        m.el.style.transform =
          'translate3d(' + m.x.toFixed(2) + 'px,' + m.y.toFixed(2) + 'px,0)';

        /* The label leans further than the button — a small internal
           parallax that sells the whole effect. */
        if (m.label) {
          m.label.style.transform =
            'translate3d(' + (m.x * 1.6).toFixed(2) + 'px,' + (m.y * 1.6).toFixed(2) + 'px,0)';
        }
      }
    }

    if (!moving) {
      running = false;
      frame = null;
      return;
    }

    frame = window.requestAnimationFrame(tick);
  }

  function start() {
    if (running || REDUCED) return;
    running = true;
    frame = window.requestAnimationFrame(tick);
  }

  function stop() {
    if (frame) window.cancelAnimationFrame(frame);
    running = false;
    frame = null;
  }

  /* ═══════════════════════════════════════════════════════════
     4. RIPPLE
     ═══════════════════════════════════════════════════════════ */

  function onRipple(e) {
    if (REDUCED) return;

    var target = e.target.closest ? e.target.closest('.ls-ripple') : null;
    if (!target) return;

    var rect = target.getBoundingClientRect();

    /* Position the wave at the exact click point, as a percentage, so it
       survives the element being resized mid-animation. */
    target.style.setProperty('--ls-rx', ((e.clientX - rect.left) / rect.width) * 100 + '%');
    target.style.setProperty('--ls-ry', ((e.clientY - rect.top) / rect.height) * 100 + '%');

    var wave = document.createElement('span');
    wave.className = 'ls-ripple__wave';
    target.appendChild(wave);

    /* Clean up. Left alone, every click leaves a dead span behind, and a
       long session on a product grid accumulates hundreds of them. */
    wave.addEventListener('animationend', function () {
      wave.remove();
    });

    window.setTimeout(function () {
      if (wave.parentNode) wave.remove();
    }, 1200);
  }

  /* ═══════════════════════════════════════════════════════════
     WIRING
     ═══════════════════════════════════════════════════════════ */

  function init() {
    initReveals(document);
    measure();

    if (!REDUCED) {
      window.addEventListener(
        'scroll',
        function () {
          start();
        },
        { passive: true }
      );

      if (FINE) {
        window.addEventListener(
          'pointermove',
          function (e) {
            pointer.x = e.clientX;
            pointer.y = e.clientY;
            start();
          },
          { passive: true }
        );
      }

      window.addEventListener('resize', scheduleMeasure, { passive: true });
      window.addEventListener('scroll', scheduleMeasure, { passive: true });

      start();
    }

    document.addEventListener('pointerdown', onRipple);

    /* Sections re-render in the theme editor, and AJAX filtering swaps the
       product grid. New nodes need observing; cached rects go stale. */
    document.addEventListener('shopify:section:load', function (e) {
      initReveals(e.target);
      scheduleMeasure();
    });

    document.addEventListener('shopify:section:unload', scheduleMeasure);
    document.addEventListener('ls:cart:updated', scheduleMeasure);

    /* The loop costs nothing in a background tab if it is not running. */
    document.addEventListener('visibilitychange', function () {
      if (document.hidden) {
        stop();
      } else {
        start();
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.LSMotion = {
    reveal: initReveals,
    refresh: measure,
    stop: stop
  };
})();